import React from "react";
import { Observer } from "mobx-react";
import { AutoSizer } from "react-virtualized";
import { Fab, Paper, withStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";
import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchResultLayout from "@search/components/SearchResultLayout";
import ActionBar from "@search/ActionBar";
import TableSearch from "@search/TableSearch/TableSearch";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";
import DnDContainer from "@containers/DnDContainer/DnDContainer";

import SharedFileSearchForm from "./SharedFileSearchForm";
import { SharedFileSearchStore } from "./SharedFileSearchStore";
import { getColumns } from "./SharedFileSearchColumns";
import SharedFilesUploadedDialog from "../SharedFilesUploadedDialog";
import SharedFilesEditDialog from "../SharedFileEdit/SharedFilesEditDialog";
import { SharedFileUploader } from "../SharedFileUploader";

type Props = {
  classes: Classes<typeof styles>;
};

class SharedFileSearch extends React.Component<Props> {
  store = new SharedFileSearchStore();

  render() {
    const { classes } = this.props;
    const columns = getColumns(this.store);

    return (
      <>
        <SearchPageLayout
          delayed
          filter={<SharedFileSearchForm manager={this.store.filterManager} />}
          actionBar={
            <ActionBar
              style={{ padding: "8px 16px" }}
              actions={[
                <Fab
                  key="new"
                  color="primary"
                  title={locale.New}
                  size="small"
                  onClick={this.store.handleUploadFile}
                >
                  <AddIcon />
                </Fab>
              ]}
            />
          }
          content={(scrollContainer) => (
            <SearchResultLayout fittedContent fitted style={{ maxWidth: 9999 }}>
              <AutoSizer disableHeight className={classes.stickyHeader}>
                {({ width }) => (
                  <div style={{ background: "white", position: "relative", width }}>
                    <ColumnSizer columns={columns} width={width}>
                      {({ ref }) => (
                        <TableSearchHeader
                          columns={columns}
                          columnSizer={ref}
                          style={{ backgroundColor: "#fff" }}
                        />
                      )}
                    </ColumnSizer>
                  </div>
                )}
              </AutoSizer>

              <TableSearch
                manager={this.store.tableManager}
                scrollContainer={scrollContainer}
                columns={getColumns(this.store)}
                rowHeight={56}
              />
            </SearchResultLayout>
          )}
          newButton={null}
          showContentOverlay={false}
          wrapContent={(content) => (
            <DnDContainer
              onDrop={this.store.addUploadFiles}
              style={{ minHeight: "100%" }}
            >
              <Paper>{content}</Paper>
            </DnDContainer>
          )}
        />

        <Observer>
          {() => (
            <SharedFilesUploadedDialog
              onClose={this.store.closeSharedFilesUploadedDialog}
              open={this.store.isUploadedFilesDialogOpen}
              files={this.store.uploadedFiles}
            />
          )}
        </Observer>

        <Observer>
          {() => <SharedFilesEditDialog store={this.store.editStore} />}
        </Observer>

        <Observer>
          {() => (
            <SharedFileUploader
              files={this.store.filesToUpload}
              onClose={this.store.closeUploaderDialog}
              onUpload={this.store.uploadFiles}
              onChangeExpiration={(fileId, expiration) =>
                this.store.updateUploadFile(fileId, { expiration })
              }
              onChangeName={(fileId, name) =>
                this.store.updateUploadFile(fileId, { name })
              }
            />
          )}
        </Observer>
      </>
    );
  }
}

const styles = (theme: ThemeType) => ({
  stickyHeader: { position: "sticky", top: 0, zIndex: 1 }
});

export default withStyles(styles as any)(SharedFileSearch);
